import { FC, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { ProgressSpinner, YellowWarningIcon } from '@repo/atomic-components';
import { DOCUMENT_ERROR_STATUSES, DOCUMENT_PROCESSING_STATUSES } from '@/constants';

/**
 * Format a status string with only the first letter capitalised,
 * preserving GDPR / AI / OCR acronyms in upper-case. Mirrors the
 * original MedicalRecordsPage helper.
 */
export const formatStatus = (status: string) =>
  status
    .toLowerCase()
    .replace(/\b(gdpr|ai|ocr)\b/gi, (match) => match.toUpperCase())
    .replace(/^./, (match) => match.toUpperCase());

interface FailureReasonTooltipProps {
  formattedStatus: string;
  failureReason: string;
}

// Status row used when a document hit a Validation Error and the BE
// returned a failure-reason string. Same visual as the non-tooltip
// error variant (yellow warning + bold label), but hovering reveals
// the failure reason via a portal-rendered tooltip that flips above
// the row when there isn't room below.
const FailureReasonTooltip: FC<FailureReasonTooltipProps> = ({ formattedStatus, failureReason }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setShowTooltip(true);

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const estimatedTooltipHeight = 80;

    if (spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow) {
      setTooltipPosition({
        top: rect.top - estimatedTooltipHeight - 8,
        left: rect.left,
      });
    } else {
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  return (
    <>
      <div
        className="relative inline-flex cursor-pointer items-center gap-2"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <YellowWarningIcon style={{ width: '16px', height: '16px' }} />
        <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>
      </div>
      {showTooltip &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[999999] w-max max-w-sm rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm text-black shadow-xl"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {failureReason}
          </div>,
          document.body,
        )}
    </>
  );
};

interface DocumentStatusCellProps {
  /** User-facing status string. SCREAMING_SNAKE_CASE values from the
   *  case-documents endpoint should be passed through
   *  `convertLodashConstantToString` first so the processing/error
   *  matchers can compare them against the constants. */
  status: string;
  /** Optional BE-supplied failure reason. When present and the status
   *  is "Validation error", surfaces in a hover tooltip. */
  failureReason?: string | null;
}

/**
 * Document-status cell used by the medical-records list table and the
 * case-page uploaded-records table. Renders one of three variants:
 *
 *  • Processing (status is in DOCUMENT_PROCESSING_STATUSES) — 16px
 *    spinner + bold black text.
 *  • Error (status is in DOCUMENT_ERROR_STATUSES) — yellow warning
 *    triangle + bold black text. Validation errors with a failure
 *    reason additionally render a hover tooltip with the reason.
 *  • Default — bold black text only.
 */
export const DocumentStatusCell: FC<DocumentStatusCellProps> = ({ status, failureReason }) => {
  if (!status || typeof status !== 'string') return null;

  const formattedStatus = formatStatus(status);
  const isProcessing = DOCUMENT_PROCESSING_STATUSES.some((s) => status.toLowerCase() === s.toLowerCase());
  const isError = DOCUMENT_ERROR_STATUSES.some((s) => status.toLowerCase() === s.toLowerCase());

  if (isError) {
    const isValidationError = status.toLowerCase() === 'validation error';
    if (isValidationError && failureReason) {
      return <FailureReasonTooltip formattedStatus={formattedStatus} failureReason={failureReason} />;
    }
    return (
      <div className="flex items-center gap-2">
        <YellowWarningIcon style={{ width: '16px', height: '16px' }} />
        <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex items-center" style={{ gap: '8px' }}>
        <div style={{ paddingLeft: 0, paddingRight: 0 }}>
          <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" animationDuration="1.5s" />
        </div>
        <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>
      </div>
    );
  }

  return <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>;
};
